import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  PieChart, 
  LineChart as LineChartIcon, 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Calendar,
  Download,
  Filter,
  ArrowUpRight,
  ArrowDownLeft,
  FileText,
  ChevronRight,
  ChevronDown,
  Printer,
  Loader2,
  BookOpen,
  History,
  Clock,
  Users,
  Star,
  Search,
  MoreVertical,
  LayoutGrid,
  Layout,
  List,
  X,
  Plus,
  Info,
  GripVertical
} from 'lucide-react';
import { Reorder, AnimatePresence, motion } from 'motion/react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  Cell,
  PieChart as RePieChart,
  Pie
} from 'recharts';
import { collection, onSnapshot, query, where, orderBy, addDoc, updateDoc, doc, serverTimestamp } from '../firebase';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { cn } from '../lib/utils';
import { getStoredLanguage, translateArabicText } from '../lib/localization';
import { useExclusiveDropdown } from '../lib/dropdowns';
import { notify } from '../services/notify';
import { ProfitLossReport } from './ProfitLossReport';
import { BalanceSheetReport } from './BalanceSheetReport';
import { BudgetManager } from './BudgetManager';
import { BudgetSummary } from './BudgetSummary';
import { BudgetVariance } from './BudgetVariance';
import { CashSummaryReport } from './CashSummaryReport';
import { ExecutiveSummaryReport } from './ExecutiveSummaryReport';
import { TrackingSummaryReport } from './TrackingSummaryReport';
import { DepreciationScheduleReport } from './DepreciationScheduleReport';
import { MovementsInEquityReport } from './MovementsInEquityReport';
import { StatementOfCashFlowsReport } from './StatementOfCashFlowsReport';
import { GeneralLedgerReport } from './GeneralLedgerReport';
import { AgedReceivablesPayablesReport } from './AgedReceivablesPayablesReport';
import { JournalReport } from './JournalReport';
import { InventoryItemSummaryReport } from './InventoryItemSummaryReport';
import { TrialBalanceReport } from './TrialBalanceReport';
import { SalesTeamPerformanceReport } from './SalesTeamPerformanceReport';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { getDefaultCurrency } from '../lib/currency';
import { Input } from './ui/input';

const getLocalAuthToken = () =>
  typeof window === 'undefined' ? null : window.localStorage.getItem('app_auth_token');

interface ReportItem {
  id: string;
  title: string;
  description?: string;
  isStarred?: boolean;
}

interface ReportCategory {
  id: string;
  title: string;
  reports: ReportItem[];
}

const REPORT_CATEGORIES: ReportCategory[] = [
  {
    id: 'financial-performance',
    title: 'Financial performance',
    reports: [
      { id: 'Budget Manager', title: 'Budget Manager' },
      { id: 'Budget Variance', title: 'Budget Variance' },
      { id: 'Budget Summary', title: 'Budget Summary' },
      { id: 'Executive Summary', title: 'Executive Summary' },
      { id: 'Cash Summary', title: 'Cash Summary' },
      { id: 'Tracking Summary', title: 'Tracking Summary' },
    ]
  },
  {
    id: 'financial-statements',
    title: 'Financial statements',
    reports: [
      { id: 'Balance Sheet', title: 'Balance Sheet', isStarred: true },
      { id: 'Blank Report', title: 'Blank Report' },
      { id: 'Depreciation Schedule', title: 'Depreciation Schedule' },
      { id: 'Disposal Schedule', title: 'Disposal Schedule' },
      { id: 'Fixed Asset Reconciliation', title: 'Fixed Asset Reconciliation' },
      { id: 'Management Report', title: 'Management Report' },
      { id: 'Movements in Equity', title: 'Movements in Equity' },
      { id: 'Profit & Loss', title: 'Profit and Loss', isStarred: true },
      { id: 'Statement of Cash Flows', title: 'Statement of Cash Flows - Direct Method' },
    ]
  },
  {
    id: 'payables-receivables',
    title: 'Payables and receivables',
    reports: [
      { id: 'Aged Payables Detail', title: 'Aged Payables Detail', isStarred: true },
      { id: 'Aged Payables Summary', title: 'Aged Payables Summary', isStarred: true },
      { id: 'Aged Receivables Detail', title: 'Aged Receivables Detail', isStarred: true },
      { id: 'Aged Receivables Summary', title: 'Aged Receivables Summary', isStarred: true },
      { id: 'Billable Expenses', title: 'Billable Expenses - Outstanding' },
      { id: 'Contact Transactions', title: 'Contact Transactions - Summary' },
      { id: 'Expense Claim Detail', title: 'Expense Claim Detail', isStarred: true },
      { id: 'Income and Expenses by Contact', title: 'Income and Expenses by Contact' },
      { id: 'Payable Invoice Detail', title: 'Payable Invoice Detail' },
      { id: 'Payable Invoice Summary', title: 'Payable Invoice Summary' },
      { id: 'Receivable Invoice Detail', title: 'Receivable Invoice Detail' },
      { id: 'Receivable Invoice Summary', title: 'Receivable Invoice Summary', isStarred: true },
    ]
  },
  {
    id: 'payroll',
    title: 'Payroll',
    reports: [
      { id: 'Pay Run by Employee', title: 'Pay Run by Employee' },
      { id: 'Pay Run by Pay Item', title: 'Pay Run by Pay Item' },
      { id: 'Pay Run by Pay Type', title: 'Pay Run by Pay Type' },
      { id: 'Pay Run Summary', title: 'Pay Run Summary', isStarred: true },
    ]
  },
  {
    id: 'reconciliations',
    title: 'Reconciliations',
    reports: [
      { id: 'Account Summary', title: 'Account Summary' },
      { id: 'Bank Reconciliation', title: 'Bank Reconciliation' },
      { id: 'Bank Summary', title: 'Bank Summary' },
      { id: 'Cash Validation', title: 'Cash Validation Customer Report' },
      { id: 'Inventory Item List', title: 'Inventory Item List' },
      { id: 'Reconciliation Reports', title: 'Reconciliation Reports' },
      { id: 'Uncoded Statement Lines', title: 'Uncoded Statement Lines' },
    ]
  },
  {
    id: 'taxes-balances',
    title: 'Taxes and balances',
    reports: [
      { id: 'Foreign Currency', title: 'Foreign Currency Gains and Losses' },
      { id: 'General Ledger Detail', title: 'General Ledger Detail' },
      { id: 'General Ledger Exceptions', title: 'General Ledger Exceptions' },
      { id: 'General Ledger Summary', title: 'General Ledger Summary' },
      { id: 'Journal Report', title: 'Journal Report' },
      { id: 'Sales Tax Report', title: 'Sales Tax Report', isStarred: true },
      { id: 'Tax Reconciliation', title: 'Tax Reconciliation' },
      { id: 'Trial Balance', title: 'Trial Balance', isStarred: true },
    ]
  },
  {
    id: 'transactions',
    title: 'Transactions',
    reports: [
      { id: 'Account Transactions', title: 'Account Transactions', isStarred: true },
      { id: 'Duplicate Statement Lines', title: 'Duplicate Statement Lines' },
      { id: 'Inventory Item Summary', title: 'Inventory Item Summary' },
      { id: 'Sales by Item', title: 'Sales by Item', isStarred: true },
    ]
  }
];

interface ReportCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const ReportCard = ({ title, description, icon, onClick }: ReportCardProps) => (
  <button 
    onClick={onClick}
    className="bg-card border border-border p-5 rounded-xl hover:shadow-md transition-all text-left group flex items-start gap-4 text-foreground shadow-sm"
  >
    <div className="w-10 h-10 bg-primary text-primary-foreground rounded-lg flex items-center justify-center group-hover:scale-105 transition-transform">
      {icon}
    </div>
    <div className="flex-1">
      <h3 className="text-lg font-bold tracking-tight mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
    <ChevronRight className="w-5 h-5 opacity-20 group-hover:opacity-100 transition-opacity self-center" />
  </button>
);

export default function ReportsView({ orgId, orgSettings, language = 'en', initialReportId, initialAccountId, initialContactId, initialStartDate, initialEndDate, onClearInitial, onNavigate }: { orgId: string, orgSettings?: any, language?: string, initialReportId?: string | null, initialAccountId?: string | null, initialContactId?: string | null, initialStartDate?: string | null, initialEndDate?: string | null, onClearInitial?: () => void, onNavigate?: (tab: string, id?: string, extra?: any) => void }) {
  const [activeReport, setActiveReport] = useState<string | null>(initialReportId || null);
  const [reportAccountId, setReportAccountId] = useState<string | null>(initialAccountId || null);
  const [reportContactId, setReportContactId] = useState<string | null>(initialContactId || null);
  const [isLoading, setIsLoading] = useState(false);
  const organizationCurrency = getDefaultCurrency(orgSettings);

  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>(REPORT_CATEGORIES.map(c => c.id));
  const [startDate, setStartDate] = useState(initialStartDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(initialEndDate || new Date().toISOString().split('T')[0]);
  const [basis, setBasis] = useState<'Accrual' | 'Cash'>('Accrual');
  const [starredReports, setStarredReports] = useState<string[]>([]);

  useEffect(() => {
    if (initialReportId) setActiveReport(initialReportId);
    setReportAccountId(initialAccountId || null);
    setReportContactId(initialContactId || null);
    if (initialStartDate) setStartDate(initialStartDate);
    if (initialEndDate) setEndDate(initialEndDate);
    
    // If navigating from other reports to view transactions, set a wider date range by default if no date provided
    if ((initialAccountId || initialContactId) && !initialStartDate) {
      setStartDate('2020-01-01'); // Wide range to show history
    }
  }, [initialReportId, initialAccountId, initialContactId, initialStartDate, initialEndDate]);

  // Load starred reports from Firestore
  useEffect(() => {
    const localToken = getLocalAuthToken();
    if (localToken) {
      const defaults = REPORT_CATEGORIES.flatMap(category =>
        category.reports.filter(report => report.isStarred).map(report => report.id)
      );
      try {
        const saved = window.localStorage.getItem('local_report_starred');
        setStarredReports(saved ? JSON.parse(saved) : defaults);
      } catch {
        setStarredReports(defaults);
      }
      return;
    }
    if (!auth.currentUser) return;

    const userRef = doc(db, 'users', auth.currentUser.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.starredReports) {
          setStarredReports(data.starredReports);
        } else {
          // Initialize with defaults if not set in Firestore yet
          const defaults: string[] = [];
          REPORT_CATEGORIES.forEach(cat => {
            cat.reports.forEach(report => {
              if (report.isStarred) defaults.push(report.id);
            });
          });
          setStarredReports(defaults);
        }
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, `users/${auth.currentUser?.uid}`);
    });

    return () => unsubscribe();
  }, []);

  const toggleStar = async (e: React.MouseEvent, reportId: string) => {
    e.stopPropagation();
    const localToken = getLocalAuthToken();
    if (!localToken && !auth.currentUser) return;

    const newStarred = starredReports.includes(reportId)
      ? starredReports.filter(id => id !== reportId)
      : [...starredReports, reportId];

    // Optimistic update
    setStarredReports(newStarred);

    try {
      if (localToken) {
        window.localStorage.setItem('local_report_starred', JSON.stringify(newStarred));
        return;
      }
      const userRef = doc(db, 'users', auth.currentUser!.uid);
      await updateDoc(userRef, {
        starredReports: newStarred
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${auth.currentUser!.uid}`);
      // Revert on error
      setStarredReports(starredReports);
    }
  };
  
  const calculateTrialBalance = () => {
    const trialBalance: Record<string, { debit: number, credit: number }> = {};
    const asOfDate = endDate;

    accounts.forEach(account => {
      trialBalance[account.name] = { debit: 0, credit: 0 };
    });

    // 1. Manual Journals
    manualJournals.forEach(journal => {
      if (journal.date <= asOfDate) {
        (journal.lines || []).forEach((line: any) => {
          const account = accounts.find(a => a.id === line.accountId || a.code === line.accountId || a.name === line.accountId);
          if (account) {
            trialBalance[account.name].debit += (line.debit || 0);
            trialBalance[account.name].credit += (line.credit || 0);
          }
        });
      }
    });

    // 2. Invoices (Accrual)
    invoices.forEach(inv => {
      if (inv.date <= asOfDate && inv.status !== 'Draft' && inv.status !== 'Void') {
        const arAccount = accounts.find(a => a.type === 'Accounts Receivable' || a.name === 'Accounts Receivable' || a.id === 'accounts_receivable');
        const arName = arAccount?.name || 'Accounts Receivable';
        
        trialBalance[arName] = trialBalance[arName] || { debit: 0, credit: 0 };
        trialBalance[arName].debit += inv.totalAmount;
        
        (inv.items || []).forEach((item: any) => {
          const account = accounts.find(a => 
            a.id === item.accountId || 
            a.id === item.account || 
            a.code === item.accountCode || 
            a.code === item.account ||
            a.name === item.accountName ||
            a.name === item.account
          );
          const accName = account?.name || item.accountName || item.account || 'Sales';
          trialBalance[accName] = trialBalance[accName] || { debit: 0, credit: 0 };
          trialBalance[accName].credit += (item.total || (item.quantity * item.unitPrice));
        });
      }
    });

    // 3. Bills (Accrual)
    bills.forEach(bill => {
      if (bill.date <= asOfDate && bill.status !== 'Draft' && bill.status !== 'Void') {
        const apAccount = accounts.find(a => a.type === 'Accounts Payable' || a.name === 'Accounts Payable' || a.id === 'accounts_payable');
        const apName = apAccount?.name || 'Accounts Payable';

        trialBalance[apName] = trialBalance[apName] || { debit: 0, credit: 0 };
        trialBalance[apName].credit += bill.totalAmount;

        (bill.items || []).forEach((item: any) => {
          const account = accounts.find(a => 
            a.id === item.accountId || 
            a.id === item.account || 
            a.code === item.accountCode || 
            a.code === item.account ||
            a.name === item.accountName ||
            a.name === item.account
          );
          const accName = account?.name || item.accountName || item.account || 'Expenses';
          trialBalance[accName] = trialBalance[accName] || { debit: 0, credit: 0 };
          trialBalance[accName].debit += (item.total || (item.quantity * item.unitPrice));
        });
      }
    });

    // 4. Payments (Transactions)
    transactions.forEach(tx => {
      if (tx.date <= asOfDate && tx.status === 'Completed') {
        const bankAccount = accounts.find(a => a.id === tx.accountId);
        if (bankAccount) {
          trialBalance[bankAccount.name] = trialBalance[bankAccount.name] || { debit: 0, credit: 0 };
          
          if (tx.invoiceId) {
            const arAccount = accounts.find(a => a.type === 'Accounts Receivable' || a.name === 'Accounts Receivable' || a.id === 'accounts_receivable');
            const arName = arAccount?.name || 'Accounts Receivable';
            
            // Receipt: Debit Bank, Credit AR
            trialBalance[bankAccount.name].debit += tx.amount;
            trialBalance[arName] = trialBalance[arName] || { debit: 0, credit: 0 };
            trialBalance[arName].credit += tx.amount;
          } else if (tx.billId) {
            const apAccount = accounts.find(a => a.type === 'Accounts Payable' || a.name === 'Accounts Payable' || a.id === 'accounts_payable');
            const apName = apAccount?.name || 'Accounts Payable';

            // Payment: Debit AP, Credit Bank
            trialBalance[apName] = trialBalance[apName] || { debit: 0, credit: 0 };
            trialBalance[apName].debit += tx.amount;
            trialBalance[bankAccount.name].credit += tx.amount;
          } else {
            // Generic
            const account = accounts.find(a => a.id === tx.categoryId || a.code === tx.category || a.name === tx.category);
            const catName = account?.name || tx.category || 'Other';

            if (tx.category === 'Sales' || tx.category === 'Income' || tx.type === 'Receive') {
              trialBalance[bankAccount.name].debit += tx.amount;
              trialBalance[catName] = trialBalance[catName] || { debit: 0, credit: 0 };
              trialBalance[catName].credit += tx.amount;
            } else {
              trialBalance[catName] = trialBalance[catName] || { debit: 0, credit: 0 };
              trialBalance[catName].debit += tx.amount;
              trialBalance[bankAccount.name].credit += tx.amount;
            }
          }
        }
      }
    });

    // Net off debits and credits for each account
    const finalTrialBalance: Record<string, { debit: number, credit: number }> = {};
    Object.entries(trialBalance).forEach(([name, balances]) => {
      const net = balances.debit - balances.credit;
      if (net > 0) finalTrialBalance[name] = { debit: net, credit: 0 };
      else if (net < 0) finalTrialBalance[name] = { debit: 0, credit: Math.abs(net) };
    });

    return finalTrialBalance;
  };
  const [transactions, setTransactions] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [manualJournals, setManualJournals] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [budgets, setBudgets] = useState<any[]>([]);
  const [trackingCategories, setTrackingCategories] = useState<any[]>([]);
  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null);
  const [isBudgetModalOpen, setIsBudgetModalOpen] = useState(false);
  const [newBudgetName, setNewBudgetName] = useState('');
  const [budgetStartDate, setBudgetStartDate] = useState(new Date().toISOString().split('T')[0].substring(0, 7)); // YYYY-MM
  const [budgetPeriod, setBudgetPeriod] = useState(12);
  const [budgetActuals, setBudgetActuals] = useState(0);
  const [budgetValues, setBudgetValues] = useState<Record<string, number>>({});
  const [isSavingBudget, setIsSavingBudget] = useState(false);

  const [showActuals, setShowActuals] = useState(false);
  const [newBudgetTrackingSelections, setNewBudgetTrackingSelections] = useState<Record<string, string>>({});

  // New report states
  const [compareWith, setCompareWith] = useState<string>('None');
  const [comparePeriods, setComparePeriods] = useState(0);
  const [showAccountCodes, setShowAccountCodes] = useState(false);
  const [showDecimals, setShowDecimals] = useState(true);
  const [showZeroBalance, setShowZeroBalance] = useState(false);
  const [showAccountingBasis, setShowAccountingBasis] = useState(false);
  const [showYtd, setShowYtd] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>(['Cash received', 'Cash spent', 'Closing bank balance']);
  const [trackingSummaryBasis, setTrackingSummaryBasis] = useState<'Accrual' | 'Cash'>('Accrual');
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const announceDropdownOpen = useExclusiveDropdown('reports', () => {
    setIsFilterMenuOpen(false);
    setIsMoreMenuOpen(false);
  });
  const setExclusiveFilterMenuOpen = (open: boolean) => {
    if (open) announceDropdownOpen();
    setIsFilterMenuOpen(open);
    if (open) setIsMoreMenuOpen(false);
  };
  const setExclusiveMoreMenuOpen = (open: boolean) => {
    if (open) announceDropdownOpen();
    setIsMoreMenuOpen(open);
    if (open) setIsFilterMenuOpen(false);
  };
  const [selectedTrackingOptions, setSelectedTrackingOptions] = useState<Record<string, string[]>>({});
  const [journalReportColumns, setJournalReportColumns] = useState<string[]>([
    'Date',
    'Journal ID',
    'Account Code',
    'Account',
    'Debit',
    'Credit',
    'Posted Date',
    'Posted By'
  ]);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [tempColumns, setTempColumns] = useState<string[]>([]);

  const activeBudget = budgets.find(b => b.id === selectedBudgetId);

  useEffect(() => {
    if (activeBudget) {
      setBudgetValues(activeBudget.values || {});
      if (activeBudget.startDate) {
        const raw = String(activeBudget.startDate).trim().substring(0, 7);
        setBudgetStartDate(raw.length === 7 ? raw : new Date().toISOString().substring(0, 7));
      }
      if (activeBudget.period) {
        setBudgetPeriod(activeBudget.period);
      }
    } else {
      setBudgetValues({});
    }
  }, [selectedBudgetId, budgets]);

  const handleSaveBudget = async () => {
    if (!selectedBudgetId) return;
    setIsSavingBudget(true);
    try {
      const localToken = getLocalAuthToken();
      if (localToken) {
        const response = await fetch(`/api/organizations/${orgId}/budgets/${selectedBudgetId}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${localToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ values: budgetValues })
        });
        const savedBudget = await response.json().catch(() => null);
        if (!response.ok) throw new Error(savedBudget?.error || 'Could not save budget');
        setBudgets(current => current.map(budget => budget.id === selectedBudgetId ? savedBudget : budget));
      } else {
        await updateDoc(doc(db, 'budgets', selectedBudgetId), {
          values: budgetValues,
          updatedAt: serverTimestamp()
        });
      }
      notify.confirm('Budget saved successfully');
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, 'budgets');
    } finally {
      setIsSavingBudget(false);
    }
  };

  const handleCreateBudget = async () => {
    if (!newBudgetName.trim()) return;
    setIsSavingBudget(true);
    try {
      const localToken = getLocalAuthToken();
      let budgetId: string;
      if (localToken) {
        const response = await fetch(`/api/organizations/${orgId}/budgets`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: newBudgetName,
            trackingSelections: newBudgetTrackingSelections,
            startDate: budgetStartDate,
            period: budgetPeriod,
            values: {}
          })
        });
        const budget = await response.json().catch(() => null);
        if (!response.ok) throw new Error(budget?.error || 'Could not create budget');
        setBudgets(current => [budget, ...current]);
        budgetId = budget.id;
      } else {
        const docRef = await addDoc(collection(db, 'budgets'), {
          orgId,
          name: newBudgetName,
          trackingSelections: newBudgetTrackingSelections,
          startDate: budgetStartDate,
          period: budgetPeriod,
          values: {},
          createdBy: auth.currentUser?.uid,
          createdAt: serverTimestamp()
        });
        budgetId = docRef.id;
      }
      setSelectedBudgetId(budgetId);
      setIsBudgetModalOpen(false);
      setNewBudgetName('');
      setNewBudgetTrackingSelections({});
      notify.confirm('New budget created');
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'budgets');
    } finally {
      setIsSavingBudget(false);
    }
  };

  const getBudgetMonths = () => {
    try {
      const rawStr = (budgetStartDate || '').toString().trim();
      let startYear: number;
      let startMonth: number;

      if (rawStr.length >= 7 && rawStr.includes('-')) {
        const parts = rawStr.split('-');
        startYear = parseInt(parts[0], 10);
        startMonth = parseInt(parts[1], 10) - 1;
      } else {
        const now = new Date();
        startYear = now.getFullYear();
        startMonth = now.getMonth();
      }

      if (isNaN(startYear) || isNaN(startMonth)) {
        const now = new Date();
        startYear = now.getFullYear();
        startMonth = now.getMonth();
      }

      const periodCount = typeof budgetPeriod === 'number' && budgetPeriod > 0 ? Math.min(budgetPeriod, 60) : 12;
      const result: string[] = [];

      for (let i = 0; i < periodCount; i++) {
        const dateObj = new Date(startYear, startMonth + i, 1);
        const y = dateObj.getFullYear();
        const m = String(dateObj.getMonth() + 1).padStart(2, '0');
        result.push(`${y}-${m}`);
      }

      return Array.from(new Set(result));
    } catch (err) {
      console.error('Error computing budget months:', err);
      const now = new Date();
      return [`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`];
    }
  };

  const budgetMonths = getBudgetMonths();

  const exportToCSV = (data: any[], filename: string) => {
    if (data.length === 0) return;
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map(row => headers.map(header => {
        const val = row[header];
        return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
      }).join(','))
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExport = () => {
    if (!activeReport) return;
    let data: any[] = [];
    const filename = String(activeReport || '').replace(/\s+/g, '_').toLowerCase();

    if (activeReport === 'Profit & Loss') {
      const pl = calculateProfitLoss();
      data.push({ Category: 'Income', Account: '', Amount: '' });
      Object.entries(pl.incomeByAccount).forEach(([acc, amt]) => data.push({ Category: '', Account: acc, Amount: amt }));
      data.push({ Category: 'Total Income', Account: '', Amount: pl.totalIncome });
      data.push({ Category: 'Direct Costs', Account: '', Amount: '' });
      Object.entries(pl.directCostByAccount).forEach(([acc, amt]) => data.push({ Category: '', Account: acc, Amount: amt }));
      data.push({ Category: 'Total Direct Costs', Account: '', Amount: pl.totalDirectCosts });
      data.push({ Category: 'Gross Profit', Account: '', Amount: pl.totalIncome - pl.totalDirectCosts });
      data.push({ Category: 'Expenses', Account: '', Amount: '' });
      Object.entries(pl.expenseByAccount).forEach(([acc, amt]) => data.push({ Category: '', Account: acc, Amount: amt }));
      data.push({ Category: 'Total Expenses', Account: '', Amount: pl.totalExpenses });
      data.push({ Category: 'Net Profit', Account: '', Amount: (pl.totalIncome - pl.totalDirectCosts) - pl.totalExpenses });
    } else if (activeReport === 'Balance Sheet') {
      const bs = calculateBalanceSheet();
      data.push({ Category: 'Assets', Account: '', Amount: '' });
      Object.entries(bs.assets).forEach(([acc, amt]) => data.push({ Category: '', Account: acc, Amount: amt }));
      data.push({ Category: 'Total Assets', Account: '', Amount: bs.totalAssets });
      data.push({ Category: 'Liabilities', Account: '', Amount: '' });
      Object.entries(bs.liabilities).forEach(([acc, amt]) => data.push({ Category: '', Account: acc, Amount: amt }));
      data.push({ Category: 'Total Liabilities', Account: '', Amount: bs.totalLiabilities });
      data.push({ Category: 'Equity', Account: '', Amount: '' });
      Object.entries(bs.equity).forEach(([acc, amt]) => data.push({ Category: '', Account: acc, Amount: amt }));
      data.push({ Category: 'Total Equity', Account: '', Amount: bs.totalEquity });
    } else if (activeReport === 'Trial Balance') {
      const tb = calculateTrialBalance();
      Object.entries(tb).forEach(([name, balances]) => {
        data.push({ Account: name, Debit: balances.debit, Credit: balances.credit });
      });
    } else if (activeReport.includes('Aged')) {
      const isAR = activeReport.includes('Receivables');
      const isDetail = activeReport.includes('Detail');
      const agingData = calculateAging(isAR ? invoices : bills, isAR ? 'Receivable' : 'Payable');
      Object.entries(agingData).forEach(([period, amount]) => {
        data.push({ Period: period, Amount: amount });
      });
    } else if (activeReport === 'General Ledger Summary' || activeReport === 'General Ledger Detail' || activeReport === 'Account Transactions' || activeReport === 'General Ledger') {
      const isWithinRange = (date: string) => date >= startDate && date <= endDate;
      data = transactions.filter(tx => isWithinRange(tx.date)).map(tx => ({
        Date: tx.date,
        Account: tx.accountName || accounts.find(a => a.id === tx.accountId)?.name || 'Unknown',
        Description: tx.description || tx.reference || '-',
        Reference: tx.reference || '-',
        Debit: tx.type === 'Debit' ? tx.amount : 0,
        Credit: tx.type === 'Credit' ? tx.amount : 0,
        'Action By': tx.actionLog?.[0]?.user || tx.createdByName || tx.createdBy || 'System'
      }));
    } else if (activeReport === 'Inventory Item Summary') {
      items.forEach(item => {
        (item.stockRecords || []).forEach((sr: any) => {
          data.push({
            Item: item.name,
            Code: item.code || '-',
            Warehouse: sr.warehouseName || sr.warehouseId || '-',
            Lot: sr.lotNumber || '-',
            Expiry: sr.expiryDate || '-',
            Quantity: sr.quantity,
            'Unit Cost': sr.unitCost || item.costPrice || 0,
            Value: (sr.quantity * (sr.unitCost || item.costPrice || 0)),
            'Last Action By': item.actionLog?.[0]?.user || item.createdByName || item.createdBy || 'System'
          });
        });
      });
    }

    exportToCSV(data, filename);
    notify.confirm(`${activeReport} exported successfully`);
  };

  const toggleCategory = (id: string) => {
    setExpandedCategories(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (!orgId) return;

    const localToken = getLocalAuthToken();
    if (localToken) {
      let cancelled = false;
      void Promise.all([
        fetch(`/api/organizations/${orgId}/budgets`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/documents?types=invoice,repeating_invoice,credit_note,receipt`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/documents?types=bill,repeating_bill`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/users`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/chart-of-accounts`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/manual-journals`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/items`, { headers: { Authorization: `Bearer ${localToken}` } }),
        fetch(`/api/organizations/${orgId}/contacts`, { headers: { Authorization: `Bearer ${localToken}` } })
      ])
        .then(async responses => {
          if (responses.some(response => !response.ok)) throw new Error('Could not load local report data');
          return Promise.all(responses.map(response => response.json()));
        })
        .then(([localBudgets, localInvoices, localBills, localUsers, localAccounts, localJournals, localItems, localContacts]) => {
          if (cancelled) return;
          const budgetsList = Array.isArray(localBudgets) ? localBudgets : [];
          setBudgets(budgetsList);
          setSelectedBudgetId(current => current || budgetsList[0]?.id || null);
          setInvoices(Array.isArray(localInvoices) ? localInvoices : []);
          setBills(Array.isArray(localBills) ? localBills : []);
          setUsers(Array.isArray(localUsers) ? localUsers : []);
          setAccounts(Array.isArray(localAccounts) ? localAccounts : []);
          setManualJournals((Array.isArray(localJournals) ? localJournals : []).filter(journal => journal.status === 'posted' || journal.status === 'Posted'));
          setItems(Array.isArray(localItems) ? localItems : []);
          setContacts(Array.isArray(localContacts) ? localContacts : []);
          // Tracking categories and warehouses have no local table yet; keeping
          // them empty prevents Reports from creating legacy Firestore listeners.
          setTrackingCategories([]);
          setWarehouses([]);
          setTransactions([]);
        })
        .catch(error => {
          if (!cancelled) console.error('Local API error loading report data:', error);
        });
      return () => { cancelled = true; };
    }

    const qBudgets = query(
      collection(db, 'budgets'),
      where('orgId', '==', orgId)
    );
    const unsubscribeBudgets = onSnapshot(qBudgets, (snapshot) => {
      const budgetList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBudgets(budgetList);
      if (budgetList.length > 0 && !selectedBudgetId) {
        setSelectedBudgetId(budgetList[0].id);
      }
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'budgets'));

    const qTracking = query(
      collection(db, 'tracking_categories'),
      where('orgId', '==', orgId)
    );
    const unsubscribeTracking = onSnapshot(qTracking, (snapshot) => {
      setTrackingCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'tracking_categories'));

    const qTxs = query(
      collection(db, 'transactions'), 
      where('orgId', '==', orgId),
      orderBy('date', 'desc')
    );
    const unsubscribeTxs = onSnapshot(qTxs, (snapshot) => {
      setTransactions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'transactions'));

    const qInvoices = query(
      collection(db, 'documents'), 
      where('orgId', '==', orgId),
      where('type', 'in', ['invoice', 'repeating_invoice', 'credit_note', 'RECEIPT']),
      orderBy('date', 'desc')
    );
    const unsubscribeInvoices = onSnapshot(qInvoices, (snapshot) => {
      setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'documents'));

    const qBills = query(
      collection(db, 'documents'), 
      where('orgId', '==', orgId),
      where('type', '==', 'bill'),
      orderBy('date', 'desc')
    );
    const unsubscribeBills = onSnapshot(qBills, (snapshot) => {
      setBills(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'documents'));

    const qUsers = query(
      collection(db, 'org_users'),
      where('orgId', '==', orgId)
    );
    const unsubscribeUsers = onSnapshot(qUsers, (snapshot) => {
      setUsers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'org_users'));

    const qAccounts = query(
      collection(db, 'chart_of_accounts'),
      where('orgId', '==', orgId)
    );
    const unsubscribeAccounts = onSnapshot(qAccounts, (snapshot) => {
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'chart_of_accounts'));

    const qJournals = query(
      collection(db, 'manual_journals'),
      where('orgId', '==', orgId),
      where('status', '==', 'Posted')
    );
    const unsubscribeJournals = onSnapshot(qJournals, (snapshot) => {
      setManualJournals(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'manual_journals'));

    const qItems = query(collection(db, 'items'), where('orgId', '==', orgId));
    const unsubscribeItems = onSnapshot(qItems, (snapshot) => {
      setItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'items'));

    const qWarehouses = query(collection(db, 'warehouses'), where('orgId', '==', orgId));
    const unsubscribeWarehouses = onSnapshot(qWarehouses, (snapshot) => {
      setWarehouses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'warehouses'));

    const qContacts = query(collection(db, 'contacts'), where('orgId', '==', orgId));
    const unsubscribeContacts = onSnapshot(qContacts, (snapshot) => {
      setContacts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => handleFirestoreError(error, OperationType.LIST, 'contacts'));

    return () => {
      unsubscribeTxs();
      unsubscribeInvoices();
      unsubscribeBills();
      unsubscribeUsers();
      unsubscribeAccounts();
      unsubscribeJournals();
      unsubscribeBudgets();
      unsubscribeTracking();
      unsubscribeItems();
      unsubscribeWarehouses();
      unsubscribeContacts();
    };
  }, [orgId]);

  const calculateBalanceSheet = () => {
    const assets: Record<string, number> = {};
    const liabilities: Record<string, number> = {};
    const equity: Record<string, number> = {};

    const asOfDate = endDate;

    // 1. Accounts Receivable (from Invoices)
    let totalAR = 0;
    invoices.forEach(inv => {
      if (inv.date <= asOfDate && inv.status !== 'Draft' && inv.status !== 'Void') {
        const unpaid = inv.totalAmount - (inv.paymentReceived || 0);
        totalAR += unpaid;
      }
    });
    if (totalAR !== 0) assets['Accounts Receivable'] = totalAR;

    // 2. Accounts Payable (from Bills)
    let totalAP = 0;
    bills.forEach(bill => {
      if (bill.date <= asOfDate && bill.status !== 'Draft' && bill.status !== 'Void') {
        const unpaid = bill.totalAmount - (bill.paymentReceived || 0);
        totalAP += unpaid;
      }
    });
    if (totalAP !== 0) liabilities['Accounts Payable'] = totalAP;

    // 3. Bank Balances (from Transactions)
    const bankBalances: Record<string, number> = {};
    transactions.forEach(tx => {
      if (tx.date <= asOfDate && tx.status === 'Completed') {
        const account = accounts.find(a => a.id === tx.accountId);
        if (account && account.type === 'Bank') {
          if (tx.invoiceId) {
            bankBalances[account.name] = (bankBalances[account.name] || 0) + tx.amount;
          } else if (tx.billId) {
            bankBalances[account.name] = (bankBalances[account.name] || 0) - tx.amount;
          } else {
            if (tx.category === 'Sales' || tx.category === 'Income') {
              bankBalances[account.name] = (bankBalances[account.name] || 0) + tx.amount;
            } else {
              bankBalances[account.name] = (bankBalances[account.name] || 0) - tx.amount;
            }
          }
        }
      }
    });
    Object.entries(bankBalances).forEach(([name, balance]) => {
      assets[name] = balance;
    });

    // 4. Manual Journals (for all other accounts)
    manualJournals.forEach(journal => {
      if (journal.date <= asOfDate) {
        (journal.lines || []).forEach((line: any) => {
          const account = accounts.find(a => a.id === line.accountId || a.code === line.accountId);
          if (account) {
            const amount = (line.debit || 0) - (line.credit || 0);
            if (account.category === 'Assets') {
              assets[account.name] = (assets[account.name] || 0) + amount;
            } else if (account.category === 'Liabilities') {
              liabilities[account.name] = (liabilities[account.name] || 0) - amount;
            } else if (account.category === 'Equity') {
              equity[account.name] = (equity[account.name] || 0) - amount;
            }
          }
        });
      }
    });

    // 5. Retained Earnings
    equity['Retained Earnings'] = calculateProfitLoss().netProfit;

    const totalAssets = Object.values(assets).reduce((a, b) => a + b, 0);
    const totalLiabilities = Object.values(liabilities).reduce((a, b) => a + b, 0);
    const totalEquity = Object.values(equity).reduce((a, b) => a + b, 0);

    return { assets, liabilities, equity, totalAssets, totalLiabilities, totalEquity };
  };

  const calculateAging = (docs: any[], type: 'Receivable' | 'Payable') => {
    const aging = {
      current: 0,
      '1-30': 0,
      '31-60': 0,
      '61-90': 0,
      '90+': 0
    };

    const today = new Date(endDate);

    docs.forEach(doc => {
      if (doc.status === 'Awaiting Payment') {
        const unpaid = doc.totalAmount - (doc.paymentReceived || 0);
        const dueDate = new Date(doc.dueDate || doc.date);
        const diffDays = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 3600 * 24));

        if (diffDays <= 0) aging.current += unpaid;
        else if (diffDays <= 30) aging['1-30'] += unpaid;
        else if (diffDays <= 60) aging['31-60'] += unpaid;
        else if (diffDays <= 90) aging['61-90'] += unpaid;
        else aging['90+'] += unpaid;
      }
    });

    return aging;
  };

    const calculateProfitLoss = () => {
    let totalIncome = 0;
    let totalDirectCosts = 0;
    let totalExpenses = 0;
    const incomeByAccount: Record<string, number> = {};
    const directCostByAccount: Record<string, number> = {};
    const expenseByAccount: Record<string, number> = {};

    const isWithinRange = (date: string) => {
      return date >= startDate && date <= endDate;
    };

    if (basis === 'Accrual') {
      // Accrual Basis: Invoices and Bills
      invoices.forEach(inv => {
        if (isWithinRange(inv.date) && inv.status !== 'Draft' && inv.status !== 'Void') {
          (inv.items || []).forEach((item: any) => {
            const amount = item.total || (item.quantity * item.unitPrice);
            const account = accounts.find(a => a.id === item.accountId || a.id === item.account || a.code === item.accountCode || a.code === item.account || a.name === item.accountName || a.name === item.account);
            if (account?.type === 'Direct Cost') {
              totalDirectCosts += amount;
              directCostByAccount[account.name] = (directCostByAccount[account.name] || 0) + amount;
            } else if (account?.type === 'Sales' || account?.type === 'Other Income' || account?.category === 'Revenue') {
              totalIncome += amount;
              incomeByAccount[account.name] = (incomeByAccount[account.name] || 0) + amount;
            } else {
              totalIncome += amount;
              const accName = account?.name || item.accountName || item.account || 'Sales';
              incomeByAccount[accName] = (incomeByAccount[accName] || 0) + amount;
            }
          });
        }
      });

      bills.forEach(bill => {
        if (isWithinRange(bill.date) && bill.status !== 'Draft' && bill.status !== 'Void') {
          (bill.items || []).forEach((item: any) => {
            const amount = item.total || (item.quantity * item.unitPrice);
            const account = accounts.find(a => a.id === item.accountId || a.id === item.account || a.code === item.accountCode || a.code === item.account || a.name === item.accountName || a.name === item.account);
            if (account?.type === 'Direct Cost') {
              totalDirectCosts += amount;
              directCostByAccount[account.name] = (directCostByAccount[account.name] || 0) + amount;
            } else {
              totalExpenses += amount;
              const accName = account?.name || item.accountName || item.account || 'Expenses';
              expenseByAccount[accName] = (expenseByAccount[accName] || 0) + amount;
            }
          });
        }
      });

      // Manual Journals
      manualJournals.forEach(journal => {
        if (isWithinRange(journal.date)) {
          (journal.lines || []).forEach((line: any) => {
            const account = accounts.find(a => a.id === line.accountId || a.code === line.accountId || a.name === line.accountId);
            if (account) {
              if (account.type === 'Sales' || account.type === 'Other Income' || account.category === 'Revenue') {
                const amount = (line.credit || 0) - (line.debit || 0);
                totalIncome += amount;
                incomeByAccount[account.name] = (incomeByAccount[account.name] || 0) + amount;
              } else if (account.type === 'Direct Cost') {
                const amount = (line.debit || 0) - (line.credit || 0);
                totalDirectCosts += amount;
                directCostByAccount[account.name] = (directCostByAccount[account.name] || 0) + amount;
              } else if (account.category === 'Expense' || account.type === 'Expense' || account.type === 'Overheads' || account.type === 'Other Expense') {
                const amount = (line.debit || 0) - (line.credit || 0);
                totalExpenses += amount;
                expenseByAccount[account.name] = (expenseByAccount[account.name] || 0) + amount;
              }
            }
          });
        }
      });
    } else {
      // Cash Basis: Transactions
      transactions.forEach(tx => {
        if (isWithinRange(tx.date) && tx.status === 'Completed') {
          const account = accounts.find(a => a.id === tx.categoryId || a.code === tx.category || a.name === tx.category);
          const amount = Math.abs(tx.amount);
          
          if (tx.type === 'receive' || tx.type === 'Credit' || tx.type === 'Receipt' || (tx.type === 'Receive' && tx.amount > 0)) {
            if (account?.type === 'Direct Cost') {
              totalDirectCosts -= amount;
              directCostByAccount[account?.name || tx.category] = (directCostByAccount[account?.name || tx.category] || 0) - amount;
            } else {
              totalIncome += amount;
              const accName = account?.name || tx.category || 'Sales';
              incomeByAccount[accName] = (incomeByAccount[accName] || 0) + amount;
            }
          } else {
            if (account?.type === 'Direct Cost') {
              totalDirectCosts += amount;
              directCostByAccount[account?.name || tx.category] = (directCostByAccount[account?.name || tx.category] || 0) + amount;
            } else {
              totalExpenses += amount;
              const accName = account?.name || tx.category || 'Expenses';
              expenseByAccount[accName] = (expenseByAccount[accName] || 0) + amount;
            }
          }
        }
      });
    }

    const grossProfit = totalIncome - totalDirectCosts;
    const netProfit = grossProfit - totalExpenses;
    const expenseBreakdown = Object.entries(expenseByAccount).map(([name, value]) => ({ name, value }));

    return { 
      totalIncome, 
      totalDirectCosts,
      totalExpenses, 
      grossProfit,
      netProfit, 
      incomeByAccount, 
      directCostByAccount,
      expenseByAccount, 
      expenseBreakdown 
    };
  };

  const { 
    totalIncome, 
    totalDirectCosts,
    totalExpenses, 
    grossProfit,
    netProfit, 
    incomeByAccount, 
    directCostByAccount,
    expenseByAccount, 
    expenseBreakdown 
  } = calculateProfitLoss();

  const COLORS = ['#141414', 'var(--primary)', '#10b981', '#f59e0b', '#ef4444'];

  // Pre-calculate actuals for all accounts and months
  const actualsMap = React.useMemo(() => {
    const map: Record<string, number> = {};
    
    budgetMonths.forEach(month => {
      const startOfMonth = month + '-01';
      const endOfMonth = month + '-31';

      invoices.forEach(inv => {
        if (inv.date >= startOfMonth && inv.date <= endOfMonth && inv.status !== 'Draft' && inv.status !== 'Void') {
          (inv.items || []).forEach((item: any) => {
            const account = accounts.find(a => 
              a.id === item.accountId || 
              a.id === item.account || 
              a.code === item.accountCode || 
              a.code === item.account ||
              a.name === item.accountName ||
              a.name === item.account
            );
            const accountIdOrCode = account?.id || account?.code || item.accountId || item.accountCode;
            if (accountIdOrCode) {
              const key = `${accountIdOrCode}_${month}`;
              map[key] = (map[key] || 0) + (item.total || (item.quantity * item.unitPrice));
            }
          });
        }
      });

      bills.forEach(bill => {
        if (bill.date >= startOfMonth && bill.date <= endOfMonth && bill.status !== 'Draft' && bill.status !== 'Void') {
          (bill.items || []).forEach((item: any) => {
            const account = accounts.find(a => 
              a.id === item.accountId || 
              a.id === item.account || 
              a.code === item.accountCode || 
              a.code === item.account ||
              a.name === item.accountName ||
              a.name === item.account
            );
            const accountIdOrCode = account?.id || account?.code || item.accountId || item.accountCode;
            if (accountIdOrCode) {
              const key = `${accountIdOrCode}_${month}`;
              map[key] = (map[key] || 0) + (item.total || (item.quantity * item.unitPrice));
            }
          });
        }
      });

      manualJournals.forEach(journal => {
        if (journal.date >= startOfMonth && journal.date <= endOfMonth) {
          (journal.lines || []).forEach((line: any) => {
            const account = accounts.find(a => a.id === line.accountId || a.code === line.accountId || a.name === line.accountId);
            const accountIdOrCode = account?.id || account?.code || line.accountId || line.accountCode;
            if (accountIdOrCode) {
              const key = `${accountIdOrCode}_${month}`;
              map[key] = (map[key] || 0) + ((line.debit || 0) - (line.credit || 0));
            }
          });
        }
      });
    });

    return map;
  }, [budgetMonths, invoices, bills, manualJournals, accounts]);

  let content = null;

  if (activeReport === 'Budget Manager') {
    content = (
      <BudgetManager 
        accounts={accounts}
        actualsMap={actualsMap}
        showActuals={showActuals}
        setShowActuals={setShowActuals}
        selectedBudgetId={selectedBudgetId}
        setSelectedBudgetId={setSelectedBudgetId}
        budgets={budgets}
        setIsBudgetModalOpen={setIsBudgetModalOpen}
        isBudgetModalOpen={isBudgetModalOpen}
        budgetStartDate={budgetStartDate}
        setBudgetStartDate={setBudgetStartDate}
        budgetPeriod={budgetPeriod}
        setBudgetPeriod={setBudgetPeriod}
        trackingCategories={trackingCategories}
        activeBudget={activeBudget}
        handleSaveBudget={handleSaveBudget}
        isSavingBudget={isSavingBudget}
        budgetMonths={budgetMonths}
        budgetValues={budgetValues}
        setBudgetValues={setBudgetValues}
        newBudgetName={newBudgetName}
        setNewBudgetName={setNewBudgetName}
        newBudgetTrackingSelections={newBudgetTrackingSelections}
        setNewBudgetTrackingSelections={setNewBudgetTrackingSelections}
        handleCreateBudget={handleCreateBudget}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Budget Summary') {
    content = (
      <BudgetSummary 
        accounts={accounts}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        budgetPeriod={budgetPeriod}
        setBudgetPeriod={setBudgetPeriod}
        selectedBudgetId={selectedBudgetId}
        setSelectedBudgetId={setSelectedBudgetId}
        budgets={budgets}
        setIsBudgetModalOpen={setIsBudgetModalOpen}
        isBudgetModalOpen={isBudgetModalOpen}
        trackingCategories={trackingCategories}
        activeBudget={activeBudget}
        budgetValues={budgetValues}
        newBudgetName={newBudgetName}
        setNewBudgetName={setNewBudgetName}
        newBudgetTrackingSelections={newBudgetTrackingSelections}
        setNewBudgetTrackingSelections={setNewBudgetTrackingSelections}
        handleCreateBudget={handleCreateBudget}
        isSavingBudget={isSavingBudget}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Budget Variance') {
    content = (
      <BudgetVariance 
        accounts={accounts}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedBudgetId={selectedBudgetId}
        setSelectedBudgetId={setSelectedBudgetId}
        budgets={budgets}
        setIsBudgetModalOpen={setIsBudgetModalOpen}
        isBudgetModalOpen={isBudgetModalOpen}
        trackingCategories={trackingCategories}
        activeBudget={activeBudget}
        budgetStartDate={budgetStartDate}
        setBudgetStartDate={setBudgetStartDate}
        budgetPeriod={budgetPeriod}
        setBudgetPeriod={setBudgetPeriod}
        budgetMonths={budgetMonths}
        budgetValues={budgetValues}
        invoices={invoices}
        bills={bills}
        manualJournals={manualJournals}
        newBudgetName={newBudgetName}
        setNewBudgetName={setNewBudgetName}
        newBudgetTrackingSelections={newBudgetTrackingSelections}
        setNewBudgetTrackingSelections={setNewBudgetTrackingSelections}
        handleCreateBudget={handleCreateBudget}
        isSavingBudget={isSavingBudget}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Profit & Loss') {
    content = (
      <ProfitLossReport 
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        compareWith={compareWith}
        setCompareWith={setCompareWith}
        basis={basis}
        setBasis={setBasis}
        trackingCategories={trackingCategories}
        showAccountCodes={showAccountCodes}
        setShowAccountCodes={setShowAccountCodes}
        showDecimals={showDecimals}
        setShowDecimals={setShowDecimals}
        isFilterMenuOpen={isFilterMenuOpen}
        setIsFilterMenuOpen={setExclusiveFilterMenuOpen}
        isMoreMenuOpen={isMoreMenuOpen}
        setIsMoreMenuOpen={setExclusiveMoreMenuOpen}
        handleExport={handleExport}
        totalIncome={totalIncome}
        totalExpenses={totalExpenses}
        totalDirectCosts={totalDirectCosts}
        grossProfit={grossProfit}
        netProfit={netProfit}
        incomeByAccount={incomeByAccount}
        directCostByAccount={directCostByAccount}
        expenseByAccount={expenseByAccount}
        expenseBreakdown={expenseBreakdown}
        COLORS={COLORS}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Cash Summary') {
    content = (
      <CashSummaryReport 
        accounts={accounts}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        compareWith={compareWith}
        setCompareWith={setCompareWith}
        invoices={invoices}
        bills={bills}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Executive Summary') {
    content = (
      <ExecutiveSummaryReport 
        accounts={accounts}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        compareWith={compareWith}
        setCompareWith={setCompareWith}
        invoices={invoices}
        bills={bills}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Tracking Summary') {
    content = (
      <TrackingSummaryReport 
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        accounts={accounts}
        invoices={invoices}
        bills={bills}
        trackingCategories={trackingCategories}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Sales Team Performance') {
    content = (
      <SalesTeamPerformanceReport 
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        users={users}
        invoices={invoices}
        bills={bills}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Balance Sheet') {
    content = (
      <BalanceSheetReport 
        currency={organizationCurrency}
        endDate={endDate}
        setEndDate={setEndDate}
        compareWith={compareWith}
        setCompareWith={setCompareWith}
        basis={basis}
        setBasis={setBasis}
        showZeroBalance={showZeroBalance}
        setShowZeroBalance={setShowZeroBalance}
        showAccountingBasis={showAccountingBasis}
        setShowAccountingBasis={setShowAccountingBasis}
        showAccountCodes={showAccountCodes}
        setShowAccountCodes={setShowAccountCodes}
        showDecimals={showDecimals}
        setShowDecimals={setShowDecimals}
        isFilterMenuOpen={isFilterMenuOpen}
        setIsFilterMenuOpen={setExclusiveFilterMenuOpen}
        isMoreMenuOpen={isMoreMenuOpen}
        setIsMoreMenuOpen={setExclusiveMoreMenuOpen}
        handleExport={handleExport}
        calculateBalanceSheet={calculateBalanceSheet}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Blank Report') {
    content = (
      <div className="space-y-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <button 
            onClick={() => setActiveReport(null)}
            className="flex items-center gap-2 text-sm font-bold hover:opacity-70 transition-opacity"
          >
            <ArrowDownLeft className="w-4 h-4 rotate-45" />
            <span>Back to Reports</span>
          </button>
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap items-center gap-2 bg-white/50 border border-[#141414] px-3 sm:px-4 py-2 rounded-2xl sm:rounded-full w-full sm:w-auto">
              <Calendar className="w-4 h-4 opacity-50" />
              <span className="text-[10px] font-bold uppercase tracking-widest opacity-40">Date range:</span>
              <input 
                type="date" 
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
              />
              <span className="opacity-20">to</span>
              <input 
                type="date" 
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-transparent border-none outline-none text-[10px] font-bold uppercase tracking-widest"
              />
            </div>

            <button 
              onClick={() => setExclusiveFilterMenuOpen(!isFilterMenuOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all"
            >
              <Filter className="w-4 h-4" />
              Filter
            </button>

            <div className="relative">
              <button 
                onClick={() => setExclusiveMoreMenuOpen(!isMoreMenuOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-white/50 border border-[#141414] rounded-full text-xs font-bold hover:bg-[#141414]/5 transition-all"
              >
                <MoreVertical className="w-4 h-4" />
                More
              </button>
              
              {isMoreMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-[#141414] rounded-2xl shadow-xl z-50 p-4 space-y-4">
                  <div className="space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Accounting basis</p>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setBasis('Accrual')}
                        className={cn("flex-1 py-1 text-[10px] font-bold uppercase rounded-lg border", basis === 'Accrual' ? "bg-[#141414] text-white border-[#141414]" : "border-[#141414]/20")}
                      >
                        Accrual
                      </button>
                      <button 
                        onClick={() => setBasis('Cash')}
                        className={cn("flex-1 py-1 text-[10px] font-bold uppercase rounded-lg border", basis === 'Cash' ? "bg-[#141414] text-white border-[#141414]" : "border-[#141414]/20")}
                      >
                        Cash
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest opacity-40">Show</p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={showDecimals} onChange={(e) => setShowDecimals(e.target.checked)} className="rounded border-[#141414]" />
                      <span className="text-[10px] font-bold uppercase tracking-widest">Decimals</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <button className="px-6 py-2 bg-[#141414] text-[#E4E3E0] rounded-full text-xs font-bold hover:opacity-90 transition-all">
              Update
            </button>
          </div>
        </div>

        <div className="bg-white/50 border border-[#141414] rounded-[40px] p-12 shadow-sm">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold tracking-tighter italic serif mb-2">Untitled Report</h1>
            <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40">
              Demo Company (Global)
            </p>
            <p className="text-sm uppercase tracking-[0.3em] font-bold opacity-40 mt-1">
              For the year ended {new Date(endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          <div className="flex flex-col items-center justify-center py-20 space-y-6">
            <div className="w-64 h-64 bg-[#141414]/5 rounded-[40px] flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center opacity-10">
                <FileText className="w-48 h-48" />
              </div>
              <div className="relative z-10 text-center p-8">
                <div className="w-16 h-16 bg-[#141414] text-white rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Layout className="w-8 h-8" />
                </div>
                <p className="text-xs font-bold uppercase tracking-widest opacity-60">Layout Editor</p>
              </div>
            </div>
            <div className="text-center max-w-xs">
              <p className="text-sm font-bold mb-4 opacity-60">Create a report using the layout editor</p>
              <button className="px-8 py-3 bg-white border border-[#141414] rounded-full text-sm font-bold hover:bg-[#141414] hover:text-white transition-all">
                Edit layout
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  } else if (activeReport === 'Depreciation Schedule') {
    content = (
      <DepreciationScheduleReport 
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        isFilterMenuOpen={isFilterMenuOpen}
        setIsFilterMenuOpen={setExclusiveFilterMenuOpen}
        isMoreMenuOpen={isMoreMenuOpen}
        setIsMoreMenuOpen={setExclusiveMoreMenuOpen}
        showDecimals={showDecimals}
        setShowDecimals={setShowDecimals}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Movements in Equity') {
    content = (
      <MovementsInEquityReport 
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        compareWith={compareWith}
        setCompareWith={setCompareWith}
        isFilterMenuOpen={isFilterMenuOpen}
        setIsFilterMenuOpen={setExclusiveFilterMenuOpen}
        isMoreMenuOpen={isMoreMenuOpen}
        setIsMoreMenuOpen={setExclusiveMoreMenuOpen}
        basis={basis}
        setBasis={setBasis}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Statement of Cash Flows') {
    content = (
      <StatementOfCashFlowsReport 
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        compareWith={compareWith}
        setCompareWith={setCompareWith}
        isFilterMenuOpen={isFilterMenuOpen}
        setIsFilterMenuOpen={setExclusiveFilterMenuOpen}
        isMoreMenuOpen={isMoreMenuOpen}
        setIsMoreMenuOpen={setExclusiveMoreMenuOpen}
        showDecimals={showDecimals}
        setShowDecimals={setShowDecimals}
        setActiveReport={setActiveReport}
      />
    );
       } else if (activeReport === 'General Ledger Detail' || activeReport === 'General Ledger Summary' || activeReport === 'General Ledger' || activeReport === 'Account Transactions') {
    content = (
      <GeneralLedgerReport 
        activeReport={activeReport}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        accounts={accounts}
        contacts={contacts}
        manualJournals={manualJournals}
        invoices={invoices}
        bills={bills}
        transactions={transactions}
        reportAccountId={reportAccountId}
        setReportAccountId={setReportAccountId}
        reportContactId={reportContactId}
        setReportContactId={setReportContactId}
        setActiveReport={setActiveReport}
        onClearInitial={onClearInitial}
        onNavigate={onNavigate}
        handleExport={handleExport}
      />
    );
  } else if (activeReport === 'Aged Receivables Summary' || activeReport === 'Aged Receivables Detail' || activeReport === 'Aged Payables Summary' || activeReport === 'Aged Payables Detail' || activeReport === 'Aged Receivables' || activeReport === 'Aged Payables') {
    content = (
      <AgedReceivablesPayablesReport 
        currency={organizationCurrency}
        activeReport={activeReport}
        endDate={endDate}
        setEndDate={setEndDate}
        invoices={invoices}
        bills={bills}
        calculateAging={calculateAging}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Journal Report') {
    content = (
      <JournalReport 
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        manualJournals={manualJournals}
        users={users}
        accounts={accounts}
        journalReportColumns={journalReportColumns}
        setJournalReportColumns={setJournalReportColumns}
        setIsReorderModalOpen={setIsReorderModalOpen}
        setTempColumns={setTempColumns}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Inventory Item Summary') {
    content = (
      <InventoryItemSummaryReport 
        endDate={endDate}
        items={items}
        handleExport={handleExport}
        setActiveReport={setActiveReport}
      />
    );
  } else if (activeReport === 'Trial Balance') {
    content = (
      <TrialBalanceReport 
        endDate={endDate}
        setEndDate={setEndDate}
        accounts={accounts}
        calculateTrialBalance={calculateTrialBalance}
        handleExport={handleExport}
        setActiveReport={setActiveReport}
        setReportAccountId={setReportAccountId}
      />
    );
    } else if (activeReport) {
      content = (
        <div className="space-y-8">
          <Button variant="ghost" onClick={() => setActiveReport(null)} className="w-fit px-2 text-sm">
            <ArrowDownLeft className="w-4 h-4 rotate-45" />
            <span>Back to Reports</span>
          </Button>
          <div className="bg-card border border-border rounded-xl p-12 sm:p-24 text-center shadow-sm">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-muted-foreground animate-spin" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-2">{activeReport}</h1>
            <p className="text-sm text-muted-foreground">This report is currently being generated. Please check back soon.</p>
          </div>
        </div>
      );
    } else {
      const allReports = REPORT_CATEGORIES.flatMap(category => category.reports);
      const favouriteReports = allReports.filter(report => starredReports.includes(report.id));
      const visibleCategories = REPORT_CATEGORIES.map(category => ({
        ...category,
        reports: category.reports.filter(report => report.title.toLowerCase().includes(searchQuery.toLowerCase())),
      })).filter(category => category.reports.length > 0);

      content = (
        <div className="space-y-6">
          <div className="flex flex-col gap-5 border-b border-border pb-5">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-semibold tracking-tight text-foreground">Reports</h1>
                <p className="mt-1 text-sm text-muted-foreground">Run and customise reports to understand your business.</p>
              </div>
              <Button className="w-fit gap-2" onClick={() => setActiveReport('Blank Report')}>
                <Plus className="w-4 h-4" />
                New report
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-1 rounded-md bg-muted/60 p-1 w-fit">
                <button type="button" onClick={() => setSearchQuery('')} className={cn('px-4 py-2 rounded text-sm font-medium transition-colors', searchQuery === '' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>All reports</button>
                <button type="button" onClick={() => setSearchQuery(' ')} className={cn('px-4 py-2 rounded text-sm font-medium transition-colors', searchQuery === ' ' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>Favourites <span className="ml-1 text-xs">{favouriteReports.length}</span></button>
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="search" placeholder="Search reports" value={searchQuery === ' ' ? '' : searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 bg-card" />
              </div>
            </div>
          </div>

          {searchQuery === ' ' ? (
            <section className="bg-card border border-border rounded-md overflow-hidden">
              <div className="px-5 py-4 border-b border-border"><h2 className="font-semibold">Favourites</h2><p className="text-sm text-muted-foreground mt-1">Your most frequently used reports.</p></div>
              {favouriteReports.length === 0 ? <p className="p-8 text-sm text-muted-foreground">No favourite reports yet. Select the star beside a report to add one.</p> : (
                <div className="divide-y divide-border">{favouriteReports.map(report => <button key={report.id} type="button" onClick={() => setActiveReport(report.id)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/40"><span className="font-medium">{report.title}</span><ChevronRight className="w-4 h-4 text-muted-foreground" /></button>)}</div>
              )}
            </section>
          ) : (
            <div className="space-y-4">
              {visibleCategories.map((category) => {
                const isExpanded = expandedCategories.includes(category.id);
                return (
                  <section key={category.id} className="bg-card border border-border rounded-md overflow-hidden">
                    <button type="button" onClick={() => toggleCategory(category.id)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/40 transition-colors">
                      <div className="flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center rounded bg-primary/10 text-primary"><FileText className="w-4 h-4" /></span><div><h2 className="font-semibold">{category.title}</h2><p className="text-xs text-muted-foreground mt-0.5">{category.reports.length} available reports</p></div></div>
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    {isExpanded && <div className="border-t border-border divide-y divide-border">{category.reports.map(report => <div key={report.id} className="group flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-muted/30"><button type="button" onClick={() => setActiveReport(report.id)} className="flex-1 text-left text-sm font-medium text-primary hover:underline">{report.title}</button><button type="button" aria-label={`${starredReports.includes(report.id) ? 'Unstar' : 'Star'} ${report.title}`} onClick={(e) => toggleStar(e, report.id)} className="p-1.5 rounded hover:bg-muted"><Star className={cn('w-4 h-4', starredReports.includes(report.id) ? 'fill-primary text-primary' : 'text-muted-foreground')} /></button><MoreVertical className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100" /></div>)}</div>}
                  </section>
                );
              })}
              {visibleCategories.length === 0 && <div className="bg-card border border-border rounded-md p-10 text-center"><Search className="w-8 h-8 mx-auto text-muted-foreground mb-3" /><p className="font-medium">No reports found</p><p className="text-sm text-muted-foreground mt-1">Try a different search term.</p></div>}
            </div>
          )}
        </div>
      );
    }

  return (
    <div className="relative min-h-full bg-background text-foreground">
      {activeReport ? (
        <div className="min-h-full">
          <header className="flex items-center justify-between border-b border-border bg-card px-4 py-3 sm:px-6">
            <div className="min-w-0">
              <button type="button" onClick={() => setActiveReport(null)} className="text-xs font-medium text-primary hover:underline">Reports</button>
              <h1 className="truncate text-lg font-semibold leading-tight">{activeReport === 'Profit & Loss' ? 'Profit and Loss' : activeReport}</h1>
            </div>
            <button type="button" aria-label={`Favourite ${activeReport}`} onClick={(event) => toggleStar(event, activeReport)} className="rounded p-2 text-primary hover:bg-muted">
              <Star className={cn('h-5 w-5', starredReports.includes(activeReport) ? 'fill-primary' : '')} />
            </button>
          </header>
          <main className="mx-auto max-w-[1280px] px-4 py-5 sm:px-6 lg:px-8">
            <div className="rounded-md border border-border bg-muted/20 p-3 sm:p-5">
              {content}
            </div>
          </main>
        </div>
      ) : content}
      <AnimatePresence>
        {isReorderModalOpen && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto overscroll-contain p-4 bg-[#141414]/40 backdrop-blur-sm"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setIsReorderModalOpen(false);
            }}
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain text-foreground"
            >
              <div className="p-5 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold tracking-tight">Drag to reorder columns</h2>
                  <Info className="w-4 h-4 opacity-40" />
                </div>
                <button
                  type="button"
                  aria-label="Close reorder columns dialog"
                  onClick={() => setIsReorderModalOpen(false)}
                  className="p-2 hover:bg-muted rounded-md transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                <Reorder.Group 
                  axis="y" 
                  values={tempColumns} 
                  onReorder={setTempColumns}
                  className="space-y-2"
                >
                  {tempColumns.map((column) => (
                    <Reorder.Item 
                      key={column} 
                      value={column}
                      className="flex items-center gap-3 p-4 bg-background border border-border rounded-lg cursor-grab active:cursor-grabbing hover:border-primary/50 transition-colors group"
                    >
                      <GripVertical className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      <span className="text-sm font-medium">{column}</span>
                    </Reorder.Item>
                  ))}
                </Reorder.Group>
              </div>

              <div className="p-5 border-t border-border bg-muted/40 flex items-center justify-end gap-3">
                <Button variant="ghost" onClick={() => setIsReorderModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setJournalReportColumns(tempColumns);
                    setIsReorderModalOpen(false);
                    notify.confirm('Column order updated');
                  }}
                >
                  Apply
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
